import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { emitter } from 'shared/helpers/mitt';
import { getUserPermissions } from '../helper/permissionsHelper';
import {
  routeIsAccessibleFor,
  validateLoggedInRoutes,
  defaultRedirectPage,
} from '../helper/routeHelpers';
import {
  setAccessProfileContext,
  showAccessProfileNotice,
  accessProfileRouteName,
} from '../helper/accessProfileNotice';
import { hasConversationPermission } from '../helper/conversationPermissions';
import { conversationSessionCache } from '../helper/conversationSessionCache';
import { clearConversationPrefetchSnapshot } from '../helper/conversationPrefetchCache';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { updatePageTitle } from 'shared/helpers/pageTitle';
import {
  resolveLastModulePath,
  saveLastModule,
} from '../helper/aceleresLastModule';
import {
  INTEGRACOES_ROUTE,
  shouldForceIntegracoes,
} from '../helper/aceleresIntegracoesGate';

const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const currentAccount = accounts.find(
    account => Number(account.id) === Number(to.params.accountId)
  );
  setAccessProfileContext(user, to.params.accountId || accountId);
  if (
    to.name &&
    currentAccount?.status === 'active' &&
    !routeIsAccessibleFor(to, getUserPermissions(user, to.params.accountId))
  ) {
    const destination = frontendURL(
      defaultRedirectPage(to, getUserPermissions(user, to.params.accountId))
    );
    showAccessProfileNotice({
      pageName: accessProfileRouteName(to),
      destinationName: accessProfileRouteName(router.resolve(destination)),
    });
    return next(destination);
  }

  // Aceleres — primeiro acesso sem nenhuma conexao cadastrada abre em
  // Integracoes, venha a pessoa do login/SSO ou de um link colado direto numa
  // rota interna. Roda antes do galho de entrada para que a memoria do ultimo
  // modulo nao devolva a pessoa a uma tela que ainda nao tem dado nenhum.
  const gateAccountId = to.params?.accountId || accountId;
  const forceIntegracoes = await shouldForceIntegracoes({
    routeName: to.name,
    user,
    accountId: gateAccountId,
  });

  if (forceIntegracoes) {
    // Resolver pelo NOME da rota, e nao montar o caminho a mao: se o path de
    // Integracoes mudar em configuracoes.routes.js, o desvio acompanha em vez
    // de virar um 404 silencioso.
    return next({
      name: INTEGRACOES_ROUTE,
      params: { accountId: String(gateAccountId) },
    });
  }

  if (to.name === 'no_accounts' || !to.name) {
    // Entrada sem rota ("/", "/app", "/app/"): é aqui que cai quem reabre o
    // Zoraxy com a sessao ainda valida. Só neste galho a memoria do ultimo
    // modulo manda — URL explicita (deep link de e-mail, bookmark de conversa)
    // continua ganhando de qualquer preferencia salva.
    // A conta vem da URL quando ela traz uma (o SSO devolve /app/accounts/<id>
    // sem rota justamente para cair aqui); senão, a conta corrente do usuário.
    const targetAccountId = to.params?.accountId || accountId;
    const lastModulePath = resolveLastModulePath({
      router,
      userId: user.id,
      accountId: targetAccountId,
    });
    const allowedLastModulePath =
      lastModulePath &&
      routeIsAccessibleFor(
        router.resolve(lastModulePath),
        getUserPermissions(user, targetAccountId)
      )
        ? lastModulePath
        : null;
    return next(
      allowedLastModulePath ||
        frontendURL(
          defaultRedirectPage(
            { params: { accountId: targetAccountId } },
            getUserPermissions(user, targetAccountId)
          )
        )
    );
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');
  emitter.on('aceleres.access_profile.refresh', async data => {
    const { getCurrentUser: user } = store.getters;
    if (data?.user_id && Number(data.user_id) !== Number(user.id)) return;
    try {
      await store.dispatch('refreshAccessProfile');
      const current = router.currentRoute.value;
      if (!current.params.accountId) return;
      const refreshedUser = store.getters.getCurrentUser;
      setAccessProfileContext(refreshedUser, current.params.accountId);
      if (
        !hasConversationPermission(
          refreshedUser,
          current.params.accountId,
          'conversation_view'
        )
      ) {
        await store.dispatch('emptyAllConversations');
        await store.dispatch('clearSearchResults');
        await store.dispatch('notifications/clear');
        conversationSessionCache.clear();
        clearConversationPrefetchSnapshot({
          accountId: current.params.accountId,
          userId: refreshedUser.id,
        });
      }
      const redirect = validateLoggedInRoutes(
        current,
        store.getters.getCurrentUser
      );
      if (redirect) {
        const destination = frontendURL(redirect);
        showAccessProfileNotice({
          pageName: accessProfileRouteName(current),
          destinationName: accessProfileRouteName(router.resolve(destination)),
        });
        await router.replace(destination);
      }
    } catch {
      // An unavailable refresh preserves the session; the server still authorizes every action.
    }
  });

  router.beforeEach((to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    userAuthentication.then(() => {
      return validateAuthenticateRoutePermission(to, next, store);
    });
  });

  // Aceleres: registra o modulo de cada tela efetivamente aberta. Roda no
  // afterEach de proposito — so conta o que a navegacao aprovou, entao rota
  // barrada por permissao nunca vira destino de abertura.
  router.afterEach(to => {
    updatePageTitle(to);
    const { getCurrentUser: user } = store.getters;
    saveLastModule({
      routeName: to.name,
      accountId: to.params?.accountId,
      userId: user?.id,
    });
  });

  // A instalacao do router dispara a primeira navegacao antes de estes hooks
  // serem registrados. Sincroniza o titulo assim que essa rota inicial ficar pronta.
  router.isReady().then(() => updatePageTitle(router.currentRoute.value));
};

export default router;
